import { Button, Card, Colors, Elevation, H4, H5, Icon, Spinner } from '@blueprintjs/core'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Redirect, useParams } from 'react-router'
import styled from 'styled-components'
import SafeListService from '../../common/assets/utils/SafeListService'
import { resolvePurchaseReceiptRoute } from '../../pages/Receipts/Utils'
import InvoicesSliceReducer from '../../redux/reducers/invoice/invoices.reducer'
import { useTypedSelector } from '../../redux/reducers/selectors'
import { bffParceirosApiService } from '../../services/bff'
import { IContract, SourceProduct } from '../../services/bff/coordinator/dto/Contracts.dto'
import { FetchInvoiceSettingsResponse } from '../../services/bff/coordinator/dto/Invoices.dto'
import { RetailerDto, RetailerGroupName } from '../../services/bff/retailers/dto/Retailer.dto'
import { Loading } from '../../ui'
import { green, white } from '../../ui/utils/_colors'
import { formContainerStyle, formItemsContainerStyle } from '../styles'

const containerStyle = (backgroundColor: string) => {
  const styles = {
    ...formItemsContainerStyle,
    width: '60vw',
    height: '55vh',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: backgroundColor,
    padding: '30px',
  }
  if (window.matchMedia('(max-width: 768px)').matches) {
    styles.height = '80vh'
    styles.padding = '20px'
  }
  return styles
}

interface IApprovedPurchaseParams {
  id: string
}

const ApprovedPurchase: React.FC = () => {
  const params = useParams<IApprovedPurchaseParams>()

  const dispatch = useDispatch()
  const [contract, setContract] = useState<IContract | undefined>(undefined)
  const [retailer, setRetailer] = useState<RetailerDto | undefined>(undefined)
  const [invoiceSettings, setInvoiceSettings] = useState<FetchInvoiceSettingsResponse | undefined>(
    undefined
  )
  const [isContractLoading, setIsContractLoading] = useState<boolean>(true)
  const [isRetailerLoading, setIsRetailerLoading] = useState<boolean>(true)
  const [isInvoiceSettingsLoading, setIsInvoiceSettingsLoading] = useState<boolean>(true)
  const [redirectToCashierHome, setRedirectToCashierHome] = useState(false)
  const { isCreatingBorrowerNextInvoice } = useTypedSelector((state) => ({
    isCreatingBorrowerNextInvoice: state.invoices.isCreatingBorrowerNextInvoice,
  }))

  const fetchContract = async () => {
    if (!params.id) return

    const contract = await bffParceirosApiService.coordinator.fetchContract(params.id)
    setIsContractLoading(false)
    setContract(contract)
  }

  const fetchRetailer = async () => {
    const store = bffParceirosApiService.coordinator.getStore()
    if (store) {
      const retailer = await bffParceirosApiService.retailers.fetchRetailerById(store.retailerId)
      setIsRetailerLoading(false)
      setRetailer(retailer)
    }
  }

  const fetchInvoiceSettings = async () => {
    if (!contract) return

    const settings = await bffParceirosApiService.coordinator.fetchInvoiceSettings({
      storeId: contract.storeId,
      sourceProduct: contract.sourceProduct,
    })
    setIsInvoiceSettingsLoading(false)
    setInvoiceSettings(settings)
  }

  const setupRedirect = () => {
    setRedirectToCashierHome(true)
  }

  const createInvoice = (borrowerId: string) => {
    dispatch(InvoicesSliceReducer.actions.createBorrowerNextInvoice(borrowerId))
  }

  const exportCcb = (ccbUrl: string) => {
    window.open(ccbUrl, '_blank')
  }

  const openAwarenessTerms = (awarenessTermsUrl: string) => {
    window.open(awarenessTermsUrl, '_blank')
  }

  const renderContainer = (content: React.ReactNode) => {
    return <div style={formContainerStyle}>{content}</div>
  }

  const renderGoBackButton = () => {
    return (
      <CustomButton
        large
        style={{
          marginTop: '32px',
          backgroundColor: Colors.LIGHT_GRAY4,
          backgroundImage: 'none',
        }}
        autoFocus
        icon="arrow-left"
        onClick={setupRedirect}
        tabIndex={1}
      >
        <H5 style={{ color: Colors.GRAY1, marginBottom: 0 }}>Voltar à página inicial de Caixa</H5>
      </CustomButton>
    )
  }

  const renderLoading = renderContainer(
    <Card style={containerStyle(white.primary)} interactive={false} elevation={Elevation.TWO}>
      <div style={{ padding: '32px' }}>
        <Spinner size={75} className="text-white" />
      </div>
      <H4>Buscando contrato...</H4>
    </Card>
  )

  const renderContractNotFound = renderContainer(
    <Card style={containerStyle(green.background3)} interactive={false} elevation={Elevation.TWO}>
      <div style={{ padding: '32px' }}>
        <Icon style={{ color: white.primary }} iconSize={75} icon={'help'} />
      </div>
      <H4 style={{ color: white.primary }}>Contrato não encontrado...</H4>
      {renderGoBackButton()}
    </Card>
  )

  useEffect(() => {
    fetchContract()
    fetchRetailer()
  }, [])

  useEffect(() => {
    if (contract && !isContractLoading) {
      fetchInvoiceSettings()
    }
  }, [contract, isContractLoading])

  if (redirectToCashierHome) {
    return <Redirect to="/caixa" />
  }
  if (isContractLoading || isRetailerLoading || isInvoiceSettingsLoading) return renderLoading
  if (!contract) return renderContractNotFound

  const store = bffParceirosApiService.coordinator.getStore()
  const sourceProduct = contract.sourceProduct
  const borrowerId = contract.borrowerId

  const contractReceiptUri = resolvePurchaseReceiptRoute(params.id)

  return renderContainer(
    <Card style={containerStyle(green.background3)} interactive={false} elevation={Elevation.TWO}>
      <div style={{ padding: '32px' }}>
        <Icon style={{ color: white.primary }} iconSize={75} icon={'tick'} />
      </div>
      <H4 style={{ color: white.primary, fontFamily: 'Inter' }}>
        Compra Finalizada! Se o comprovante não abriu automaticamente,{' '}
        <a
          rel="noopener noreferrer"
          style={{ textDecoration: 'underline', color: white.primary }}
          href={contractReceiptUri}
          target="_blank"
          tabIndex={1}
        >
          CLIQUE AQUI
        </a>
        .
      </H4>
      {contract.ccbUrl && (
        <CustomButton
          large
          style={{
            marginTop: '32px',
            backgroundColor: Colors.LIGHT_GRAY4,
            backgroundImage: 'none',
          }}
          autoFocus
          icon="document"
          onClick={() => exportCcb(contract.ccbUrl!!)}
          tabIndex={2}
        >
          <H5 style={{ color: Colors.GRAY1, marginBottom: 0, fontFamily: 'Inter' }}>
            Imprimir CCB
          </H5>
        </CustomButton>
      )}
      {invoiceSettings?.enabledToGenerateBorrowerInvoice && borrowerId && (
        <CustomButton
          large
          style={{
            marginTop: '32px',
            backgroundColor: Colors.LIGHT_GRAY4,
            backgroundImage: 'none',
          }}
          autoFocus
          icon="barcode"
          onClick={() => createInvoice(borrowerId)}
          tabIndex={1}
          disabled={isCreatingBorrowerNextInvoice}
        >
          {isCreatingBorrowerNextInvoice ? (
            <Loading color="#5C7080" height={20} width={20} />
          ) : (
            <H5 style={{ color: Colors.GRAY1, marginBottom: 0, fontFamily: 'Inter' }}>
              Imprimir próximo boleto
            </H5>
          )}
        </CustomButton>
      )}
      {contract.awarenessTermsUrl && (
        <CustomButton
          large
          style={{
            marginTop: '32px',
            backgroundColor: Colors.LIGHT_GRAY4,
            backgroundImage: 'none',
          }}
          autoFocus
          onClick={() => openAwarenessTerms(contract.awarenessTermsUrl!!)}
          icon="document"
          tabIndex={1}
          disabled={false}
        >
          <H5 style={{ color: Colors.GRAY1, marginBottom: 0, fontFamily: 'Inter' }}>
            Imprimir termo de ciência
          </H5>
        </CustomButton>
      )}
      {renderGoBackButton()}
    </Card>
  )
}

export default ApprovedPurchase

const CustomButton = styled(Button)`
  width: 40%;

  @media (max-width: 768px) {
    width: 50%;
  }

  @media (max-width: 480px) {
    width: 70%;
  }
`
